import React, { useState, useEffect, useCallback } from "react";
import Select, { SingleValue } from "react-select";
import { get } from "../../utils/api";
import { buildIdFilterArray } from "../../utils/common";
import "./index.css";

// Define the structure of the properties and values used in the component.
interface CollectionProperties {
  key_mapping: string;
  key_collection: string;
  key_collection_name_field: string;
  key_document_object: string;
  key_collection_filter: object;
  value_mapping: string;
  value_collection: string;
  value_collection_name_field: string;
  value_document_object: string;
  value_collection_filter: object;
}

interface KeyValue {
  key: string;
  value: string;
}

interface ModuleFieldsKeyValueSelectProps {
  formData: any;
  properties: CollectionProperties;
  values?: KeyValue[];
  onChange: (options: KeyValue[]) => void;
}

interface Option {
  value: string;
  label: string;
}

const ModuleFieldsKeyValueSelect: React.FC<ModuleFieldsKeyValueSelectProps> = ({
  formData,
  properties,
  values = [],
  onChange,
}) => {
  const [keyOptions, setKeyOptions] = useState<Option[]>([]);
  const [valueOptions, setValueOptions] = useState<Option[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<KeyValue[]>(values);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const {
    key_mapping,
    key_collection,
    key_collection_name_field,
    key_document_object,
    key_collection_filter,
    value_mapping,
    value_collection,
    value_collection_name_field,
    value_document_object,
    value_collection_filter,
  } = properties;

  // Fetch options for both keys and values
  const fetchData = useCallback(
    async (
      mapping: string,
      collection: string,
      mapped_object: string,
      collection_filter: object,
      fieldName: string,
      setOptions: React.Dispatch<React.SetStateAction<Option[]>>
    ) => {
      setLoading(true);
      try {
        let formattedFilter = buildIdFilterArray(collection_filter, formData);
        const params = { filter: formattedFilter };
        const response = await get(`${collection}`, params);
        let data_array = response.data.data?.[0]?.[mapped_object] || [];
        const options: Option[] = data_array.map((item: any) => ({
          value: item[fieldName],
          label: item[fieldName],
        }));
        setOptions(options);
        setLoading(false);
      } catch (error) {
        console.error(`Failed to fetch data from ${collection}:`, error);
        setError(`Failed to load data from ${collection}`);
        setLoading(false);
      }
    },
    [formData] // Ensuring `formData` is part of the dependencies array
  );

  // UseEffect to initialize the component's data fetching
  useEffect(() => {
    if(formData && (Object.keys(key_collection_filter).length || Object.keys(value_collection_filter).length)) {
      fetchData(
        key_mapping,
        key_collection,
        key_document_object,
        key_collection_filter,
        key_collection_name_field,
        setKeyOptions
      );
      fetchData(
        value_mapping,
        value_collection,
        value_document_object,
        value_collection_filter,
        value_collection_name_field,
        setValueOptions
      );
    }
  }, [
    formData[Object.keys(value_collection_filter)[0]],
    key_mapping,
    key_collection,
    key_document_object,
    key_collection_filter,
    key_collection_name_field,
    value_mapping,
    value_collection,
    value_document_object,
    value_collection_filter,
    value_collection_name_field,
    // fetchData // Including fetchData as it is part of the useEffect dependency
  ]);

  // Manage selected options updates
  useEffect(() => {
    setSelectedOptions(values.length ? values : [{ key: "", value: "" }]);
  }, [values]);

  const handleAddRow = () =>
    setSelectedOptions([...selectedOptions, { key: "", value: "" }]);
  const handleRemoveRow = (index: number) => {
    const updated = selectedOptions.filter((_, i) => i !== index);
    setSelectedOptions(updated);
    onChange(updated);
  };

  const handleChange = (
    index: number,
    value: SingleValue<Option>,
    isKey: boolean
  ) => {
    const updated = [...selectedOptions];
    if (value) {
      updated[index][isKey ? "key" : "value"] = value.value;
    }
    setSelectedOptions(updated);
    onChange(updated);
  };
  const handleRetry = () => {
    setError(null);
    fetchData(
      key_mapping,
      key_collection,
      key_document_object,
      key_collection_filter,
      key_collection_name_field,
      setKeyOptions
    );
    fetchData(
      value_mapping,
      value_collection,
      value_document_object,
      value_collection_filter,
      value_collection_name_field,
      setValueOptions
    );
  };
  if (loading) return <p>Loading...</p>;
  if (error)
    return (
      <div>
        <p>{error}</p>
        <button
          onClick={handleRetry}
          style={{ padding: "8px 16px", cursor: "pointer" }}
        >
          Retry
        </button>
      </div>
    );
  return (
    <div>
      {selectedOptions.map((option, index) => (
        <div key={index} style={{ display: "flex", marginBottom: "10px" }}>
          <Select
            value={keyOptions.find((o) => o.value === option.key)}
            onChange={(selectedKey) => handleChange(index, selectedKey, true)}
            options={keyOptions}
            placeholder={"Select " + key_collection_name_field}
            className="key-select"
            classNamePrefix="select"
            isDisabled={!formData[Object.keys(key_collection_filter)[0]]}
          />
          <Select
            value={valueOptions.find((o) => o.value === option.value)}
            onChange={(selectedValue) =>
              handleChange(index, selectedValue, false)
            }
            options={valueOptions}
            placeholder={"Select " + value_collection_name_field}
            className="value-select"
            classNamePrefix="select"
            isDisabled={!formData[Object.keys(value_collection_filter)[0]]}
          />
          <button
            type="button"
            onClick={() => handleRemoveRow(index)}
            style={{ marginLeft: "10px" }}
          >
            Remove
          </button>
        </div>
      ))}
      <button type="button" onClick={handleAddRow}>
        Add Row
      </button>
    </div>
  );
};

export default ModuleFieldsKeyValueSelect;
